import type { FileUploadSubmitConfig, FileUploadSubmitSelection } from './fileUploadConfig';

/** Extract all staged File objects from RHF file field values. */
export function extractFilesFromFormValue(value: unknown): File[] {
  if (value == null) return [];
  if (value instanceof File) return [value];

  if (Array.isArray(value)) {
    return value.filter((item): item is File => item instanceof File);
  }

  if (typeof value === 'object' && 'length' in value) {
    const list = value as FileList;
    return Array.from(list).filter((item): item is File => item instanceof File);
  }

  return [];
}

/** Extract the first File from RHF file field values (FileList, File[], or File). */
export function extractFileFromFormValue(value: unknown): File | null {
  return extractFilesFromFormValue(value)[0] ?? null;
}

/** Read a browser File as raw base64 (no data-URL prefix). */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }
      const base64 = result.includes(',') ? result.split(',')[1] ?? '' : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/** Generic single-file document entry used by many API contracts. */
export async function buildNamedFileDocumentEntry(
  file: File,
): Promise<{ fileName: string; document: string }> {
  return {
    fileName: file.name,
    document: await readFileAsBase64(file),
  };
}

export function selectFilesForSubmit(
  files: File[],
  selection: FileUploadSubmitSelection = 'first',
): File[] {
  if (files.length === 0) return [];
  switch (selection) {
    case 'all':
      return files;
    case 'last':
      return [files[files.length - 1]!];
    case 'first':
    default:
      return [files[0]!];
  }
}

function readStringField(formData: Record<string, unknown>, field?: string): string | undefined {
  if (!field) return undefined;
  const value = formData[field];
  return typeof value === 'string' && value ? value : undefined;
}

function readRemovedIds(formData: Record<string, unknown>, field?: string): string[] {
  if (!field) return [];
  const value = formData[field];
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/** Map staged uploads + existing doc metadata to a generic API document envelope. */
export async function buildFileUploadEnvelope(
  formData: Record<string, unknown>,
  config: FileUploadSubmitConfig,
  options: { isUpdate?: boolean } = {},
): Promise<Record<string, unknown>> {
  const staged = extractFilesFromFormValue(formData[config.fieldName]);
  const selected = selectFilesForSubmit(staged, config.submit.selection);
  const entries = await Promise.all(selected.map((file) => buildNamedFileDocumentEntry(file)));

  const existingId = readStringField(formData, config.existingIdField);
  const existingName = readStringField(formData, config.existingNameField);
  const removedIds = readRemovedIds(formData, config.removedIdsField);

  const deleteIds = new Set<string>(removedIds);

  if (options.isUpdate && existingId) {
    const replacement = selected[0];
    if (replacement && existingName && replacement.name !== existingName) {
      deleteIds.add(existingId);
    }
    if (
      selected.length === 0 &&
      config.deleteExistingWithoutUpload?.(formData)
    ) {
      deleteIds.add(existingId);
    }
  }

  const envelope: Record<string, unknown> = {
    [config.submit.arrayKey]: entries,
  };

  if (config.submit.deleteIdsKey) {
    const ids = [...deleteIds];
    if (ids.length > 0) {
      envelope[config.submit.deleteIdsKey] = ids;
    }
  }

  return envelope;
}
